import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { ActionIcon } from "@/lib/actionIcons";
import { RichText } from "@/components/portal/RichText";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const Boxes = ({ length, value, setValue, disabled }) => (
  <div className="flex justify-center">
    <InputOTP maxLength={length} value={value} onChange={(v) => setValue(v.replace(/\D/g, ""))} disabled={disabled} inputMode="numeric" data-testid="custom-action-input">
      <InputOTPGroup className="flex-wrap justify-center">
        {Array.from({ length }, (_, i) => (
          <InputOTPSlot key={i} index={i} className={`${length > 8 ? "h-11 w-7 text-base sm:w-9" : "h-14 w-10 text-xl sm:w-12"} border-white/15 bg-black/30 font-mono-x text-slate-50`} data-testid={`custom-action-slot-${i}`} />
        ))}
      </InputOTPGroup>
    </InputOTP>
  </div>
);

const boxCount = (ca) => (ca.type === "CODIGO" ? ca.digits : ca.type === "CAMPO" && ca.field_kind === "numero" ? ca.field_length || 0 : 0);

const ValueInput = ({ ca, value, setValue, disabled }) => {
  const n = boxCount(ca);
  if (n) {
    return (
      <>
        {ca.type === "CAMPO" && ca.field_label && <label className="mb-3 block text-xs uppercase tracking-[0.16em] text-slate-400">{ca.field_label}</label>}
        <Boxes length={n} value={value} setValue={setValue} disabled={disabled} />
      </>
    );
  }
  if (ca.type === "CAMPO") {
    return (
      <div className="text-left">
        {ca.field_label && <label className="mb-2 block text-xs uppercase tracking-[0.16em] text-slate-400">{ca.field_label}</label>}
        <Input value={value} maxLength={500} inputMode={ca.field_kind === "numero" ? "numeric" : "text"}
          onChange={(e) => setValue(ca.field_kind === "numero" ? e.target.value.replace(/\D/g, "") : e.target.value)}
          disabled={disabled} className="h-12 border-white/10 bg-black/30 text-slate-50" data-testid="custom-action-input" />
      </div>
    );
  }
  return null;
};

export const CustomActionStep = ({ state, onRespond, busy, error }) => {
  const ca = state.custom_action;
  const [value, setValue] = useState("");
  const blocked = ca.status === "BLOQUEADO";
  const n = boxCount(ca);
  const ready = ca.type === "MENSAGEM" || (n ? value.length === n : value.trim().length > 0);
  const submit = (e) => { e.preventDefault(); if (ca.type !== "MENSAGEM" && ready && onRespond) onRespond(value, () => setValue("")); };

  return (
    <motion.form key={ca.instance_id} onSubmit={submit} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="glass-card w-full max-w-xl p-8 text-center sm:p-10" data-testid="custom-action-step">
      <ActionIcon icon={ca.icon} iconUrl={ca.icon_url} />
      <h1 className="mt-6 font-display text-2xl text-slate-50 sm:text-3xl" data-testid="custom-action-title">{ca.title}</h1>
      {ca.description && <RichText text={ca.description} align={ca.text_align} className="mt-4 text-slate-300" testId="custom-action-description" />}
      <div className="mt-8"><ValueInput ca={ca} value={value} setValue={setValue} disabled={blocked || busy} /></div>
      {error && <p className="mt-3 text-sm text-red-400" data-testid="custom-action-error">{error}</p>}
      {!error && ca.rejected && <p className="mt-3 text-sm text-red-400" data-testid="custom-action-rejected">Não foi possível validar os dados. Verifique e envie novamente.</p>}
      {ca.type === "CODIGO" && !blocked && ca.attempts > 0 && <p className="mt-2 text-xs text-slate-500">Tentativas restantes: {ca.max_attempts - ca.attempts}</p>}
      {ca.type !== "MENSAGEM" && (
        <button type="submit" disabled={blocked || busy || !ready} className="btn-gold mt-8 h-12 w-full" data-testid="custom-action-submit">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : ca.button_label || "Confirmar"}
        </button>
      )}
      <p className="mt-4 text-xs text-slate-500">Nunca solicitamos senhas bancárias, tokens ou dados de cartão.</p>
    </motion.form>
  );
};
